'use client';

import { useState } from 'react';
import type { GuidedQuestion, IdeaThemeType } from '@/lib/foundry/types';

interface GuidedQuestionsProps {
  questions: GuidedQuestion[];
  answers: Record<string, string>;
  onAnswerChange: (questionId: string, answer: string) => void;
  onGenerateCanvas: () => void;
  onBackToOrganized: () => void;
  isGenerating?: boolean;
  coverageScore: number;
}

const AREA_LABELS: Record<IdeaThemeType, string> = {
  problem: 'Problem',
  solution: 'Solution',
  target_customer: 'Target Customer',
  market: 'Market',
  revenue: 'Revenue',
  competition: 'Competition',
  advantage: 'Advantage',
  resource: 'Resources',
  risk: 'Risks',
  other: 'Other',
};

const AREA_ICONS: Record<IdeaThemeType, string> = {
  problem: '🎯',
  solution: '💡',
  target_customer: '👥',
  market: '📊',
  revenue: '💰',
  competition: '⚔️',
  advantage: '🛡️',
  resource: '🔧',
  risk: '⚠️',
  other: '📝',
};

function QuestionCard({
  question,
  answer,
  onAnswerChange,
  index,
}: {
  question: GuidedQuestion;
  answer: string;
  onAnswerChange: (answer: string) => void;
  index: number;
}) {
  const [isExpanded, setIsExpanded] = useState(!answer);

  const importanceColors = {
    critical: 'bg-red-500/20 text-red-300 border-red-500/40',
    important: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40',
    nice_to_have: 'bg-cream/10 text-foreground-tertiary border-border',
  };

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-start gap-4 bg-background/40 hover:bg-background/60 transition-colors text-left"
      >
        <span className="text-accent font-mono text-sm mt-1">
          Q{String(index + 1).padStart(2, '0')}
        </span>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span>{AREA_ICONS[question.area]}</span>
            <span className="text-xs text-foreground-tertiary">{AREA_LABELS[question.area]}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded border ${importanceColors[question.importance]}`}>
              {question.importance.replace('_', ' ')}
            </span>
          </div>
          <p className="text-foreground font-medium">{question.question}</p>
          {answer && !isExpanded && (
            <p className="mt-2 text-sm text-foreground-tertiary truncate">
              Your answer: {answer}
            </p>
          )}
        </div>
        <svg
          className={`w-5 h-5 text-foreground-muted transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-4 border-t border-border-muted">
          {question.context && (
            <p className="text-sm text-foreground-tertiary mb-3 italic">
              Context: {question.context}
            </p>
          )}
          <textarea
            value={answer}
            onChange={(e) => onAnswerChange(e.target.value)}
            placeholder="Type your answer here..."
            className="w-full h-24 px-3 py-2 bg-background/60 border border-border rounded-lg text-foreground placeholder-foreground/30 focus:outline-none focus:border-accent resize-none"
          />
          {question.follow_ups && question.follow_ups.length > 0 && answer && (
            <div className="mt-3 p-3 bg-background/40 rounded-lg">
              <p className="text-xs text-foreground-tertiary mb-2">Consider also:</p>
              <ul className="space-y-1">
                {question.follow_ups.map((followUp, i) => (
                  <li key={i} className="text-sm text-foreground-tertiary flex items-start gap-2">
                    <span className="text-accent">→</span>
                    {followUp}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function GuidedQuestions({
  questions,
  answers,
  onAnswerChange,
  onGenerateCanvas,
  onBackToOrganized,
  isGenerating,
  coverageScore,
}: GuidedQuestionsProps) {
  const answeredCount = Object.values(answers).filter((a) => a.trim()).length;
  const criticalQuestions = questions.filter((q) => q.importance === 'critical');
  const criticalAnswered = criticalQuestions.filter((q) => answers[q.id]?.trim()).length;
  const allCriticalAnswered = criticalAnswered === criticalQuestions.length;

  // Group questions by importance
  const groupedQuestions = {
    critical: questions.filter((q) => q.importance === 'critical'),
    important: questions.filter((q) => q.importance === 'important'),
    nice_to_have: questions.filter((q) => q.importance === 'nice_to_have'),
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-foreground mb-2">Fill in the Gaps</h2>
        <p className="text-foreground-tertiary">
          Answer these questions to strengthen your business plan
        </p>
      </div>

      {/* Progress */}
      <div className="p-4 bg-background/40 border border-border-muted rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-foreground-tertiary">Coverage Score</span>
          <span className="text-foreground font-semibold">{Math.round(coverageScore * 100)}%</span>
        </div>
        <div className="h-2 bg-cream/10 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              coverageScore >= 0.8 ? 'bg-green-500' : coverageScore >= 0.5 ? 'bg-yellow-500' : 'bg-red-500'
            }`}
            style={{ width: `${coverageScore * 100}%` }}
          />
        </div>
        <div className="mt-2 flex items-center justify-between text-xs text-foreground-muted">
          <span>{answeredCount} of {questions.length} questions answered</span>
          {criticalQuestions.length > 0 && (
            <span className={allCriticalAnswered ? 'text-green-400' : 'text-red-400'}>
              {criticalAnswered}/{criticalQuestions.length} critical
            </span>
          )}
        </div>
      </div>

      {/* Critical Questions */}
      {groupedQuestions.critical.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
            <span className="text-red-400">●</span>
            Critical Questions
          </h3>
          <div className="space-y-3">
            {groupedQuestions.critical.map((q, i) => (
              <QuestionCard
                key={q.id}
                question={q}
                answer={answers[q.id] || ''}
                onAnswerChange={(answer) => onAnswerChange(q.id, answer)}
                index={i}
              />
            ))}
          </div>
        </div>
      )}

      {/* Important Questions */}
      {groupedQuestions.important.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
            <span className="text-yellow-400">●</span>
            Important Questions
          </h3>
          <div className="space-y-3">
            {groupedQuestions.important.map((q, i) => (
              <QuestionCard
                key={q.id}
                question={q}
                answer={answers[q.id] || ''}
                onAnswerChange={(answer) => onAnswerChange(q.id, answer)}
                index={groupedQuestions.critical.length + i}
              />
            ))}
          </div>
        </div>
      )}

      {/* Nice to Have Questions */}
      {groupedQuestions.nice_to_have.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
            <span className="text-foreground-muted">●</span>
            Nice to Have
          </h3>
          <div className="space-y-3">
            {groupedQuestions.nice_to_have.map((q, i) => (
              <QuestionCard
                key={q.id}
                question={q}
                answer={answers[q.id] || ''}
                onAnswerChange={(answer) => onAnswerChange(q.id, answer)}
                index={groupedQuestions.critical.length + groupedQuestions.important.length + i}
              />
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-border-muted">
        <button
          onClick={onBackToOrganized}
          className="px-4 py-2 text-foreground-tertiary hover:text-foreground transition-colors"
        >
          ← Back to themes
        </button>
        <div className="flex items-center gap-4">
          {!allCriticalAnswered && criticalQuestions.length > 0 && (
            <span className="text-sm text-red-400">
              Answer all critical questions first
            </span>
          )}
          <button
            onClick={onGenerateCanvas}
            disabled={isGenerating || (!allCriticalAnswered && criticalQuestions.length > 0)}
            className="px-6 py-3 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {isGenerating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-navy border-t-transparent"></div>
                Generating Canvas...
              </>
            ) : (
              <>
                Generate Lean Canvas
                <span className="text-xs opacity-70">AI</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
